import { useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserCircle } from "lucide-react";

interface ProfileStepProps {
  phoneNumber: string;
  onSave: (firstName: string, lastName: string, phoneNumber: string, email?: string) => Promise<void>;
  onSkip?: () => void;
  loading: boolean;
}

export default function ProfileStep({ phoneNumber, onSave, onSkip, loading }: ProfileStepProps) {
  const { language } = useLanguage();
  const isAr = language === "ar";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const errs: Record<string, string> = {};
    if (firstName.trim().length < 2) {
      errs.firstName = isAr ? "الاسم الأول مطلوب (حرفان على الأقل)" : "First name required (min 2 chars)";
    }
    if (lastName.trim().length < 2) {
      errs.lastName = isAr ? "اسم العائلة مطلوب (حرفان على الأقل)" : "Last name required (min 2 chars)";
    }
    if (!phoneNumber.trim()) {
      errs.phoneNumber = isAr ? "رقم الجوال مطلوب" : "Phone number is required";
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errs.email = isAr ? "بريد إلكتروني غير صالح" : "Invalid email format";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSave(firstName.trim(), lastName.trim(), phoneNumber.trim(), email.trim() || undefined);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="mx-auto w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
          <UserCircle className="w-7 h-7 text-primary" />
        </div>
        <h2 className="text-xl font-bold text-foreground">
          {isAr ? "أكمل ملفك الشخصي" : "Complete your profile"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {isAr ? "أخبرنا باسمك لنخصّص تجربتك" : "Tell us your name to personalize your experience"}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>{isAr ? "رقم الجوال" : "Phone Number"}</Label>
          <Input value={phoneNumber} dir="ltr" readOnly disabled />
          {errors.phoneNumber && <p className="text-xs text-destructive">{errors.phoneNumber}</p>}
        </div>

        <div className="space-y-2">
          <Label>{isAr ? "الاسم الأول" : "First Name"} *</Label>
          <Input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={50}
            autoFocus
          />
          {errors.firstName && <p className="text-xs text-destructive">{errors.firstName}</p>}
        </div>

        <div className="space-y-2">
          <Label>{isAr ? "اسم العائلة" : "Last Name"} *</Label>
          <Input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={50}
          />
          {errors.lastName && <p className="text-xs text-destructive">{errors.lastName}</p>}
        </div>

        <div className="space-y-2">
          <Label>{isAr ? "البريد الإلكتروني (اختياري)" : "Email (optional)"}</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            dir="ltr"
          />
          {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
        </div>

        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            isAr ? "حفظ" : "Save"
          )}
        </Button>
      </form>

      {onSkip && (
        <button
          type="button"
          onClick={onSkip}
          className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          {isAr ? "تخطي" : "Skip for now"}
        </button>
      )}
    </div>
  );
}
